import type { HTMLAttributes } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '../../base/Text';
import { getDisplayImageUrl } from '@/lib/image-url';
import { IconButton } from '../../base/IconButton';
import { FavoriteToggle } from '../FavoriteToggle';
import { MoreVertical, ImageOff } from 'lucide-react';
import styles from './LinkItem.module.css';

export interface LinkItemData {
  id: string;
  url: string;
  title: string | null;
  customTitle?: string | null;
  tags?: string[];
  description?: string | null;
  image?: string | null;
  isFavorite: boolean;
  createdAt: string;
}

interface LinkItemProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  link: LinkItemData;
  onClick?: (link: LinkItemData) => void;
  onFavoriteToggle?: (link: LinkItemData) => void;
  onMenuClick?: (link: LinkItemData) => void;
}

const formatTimeAgo = (
  dateString: string,
  t: (key: string, options?: Record<string, unknown>) => string
): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return t('links.justNow');
  if (diffInSeconds < 3600) return t('links.minutesAgo', { count: Math.floor(diffInSeconds / 60) });
  if (diffInSeconds < 86400) return t('links.hoursAgo', { count: Math.floor(diffInSeconds / 3600) });
  if (diffInSeconds < 604800) return t('links.daysAgo', { count: Math.floor(diffInSeconds / 86400) });

  return date.toLocaleDateString();
};

const getDomain = (url: string): string => {
  try {
    const domain = new URL(url).hostname;
    return domain.replace(/^www\./, '');
  } catch {
    return url;
  }
};

export const LinkItem = ({
  link,
  onClick,
  onFavoriteToggle,
  onMenuClick,
  className = '',
  ...props
}: LinkItemProps) => {
  const { t } = useTranslation();
  const [imageError, setImageError] = useState(false);
  const displayImageUrl = getDisplayImageUrl(link.image);
  const hasImage = Boolean(displayImageUrl);
  const showImage = hasImage && !imageError;

  const classNames = [
    styles.item,
    onClick && styles.clickable,
    className,
  ].filter(Boolean).join(' ');

  const handleClick = () => {
    if (onClick) {
      onClick(link);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick(link);
    }
  };

  return (
    <div
      className={classNames}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      {...props}
    >
      <div className={styles.row}>
        {showImage ? (
          <img
            src={displayImageUrl!}
            alt=""
            className={styles.thumbnail}
            referrerPolicy="no-referrer"
            onError={() => setImageError(true)}
          />
        ) : hasImage && imageError ? (
          <div className={styles.thumbnailPlaceholder} title="Image unavailable">
            <ImageOff size={24} />
          </div>
        ) : null}
        <div className={styles.content}>
          <Text variant="title" className={styles.title}>
            {link.customTitle || link.title || getDomain(link.url)}
          </Text>
          <div className={styles.meta}>
            <Text variant="meta" className={styles.domain}>{getDomain(link.url)}</Text>
            <span className={styles.separator}>·</span>
            <Text variant="meta" className={styles.timeAgo}>{formatTimeAgo(link.createdAt, t)}</Text>
          </div>
        </div>
        <div className={styles.actions}>
          {onFavoriteToggle && (
            <FavoriteToggle
              isFavorite={link.isFavorite}
              onToggle={() => onFavoriteToggle(link)}
            />
          )}
          {onMenuClick && (
            <IconButton
              variant="ghost"
              size="sm"
              label="More options"
              onClick={(e) => {
                e.stopPropagation();
                onMenuClick(link);
              }}
            >
              <MoreVertical size={20} />
            </IconButton>
          )}
        </div>
      </div>
      {link.tags && link.tags.length > 0 && (
        <div className={styles.tags}>
          {link.tags.map((tag, index) => (
            <span key={index} className={styles.tag}>
              #{tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
